import { styled } from '@linaria/react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, getCurrency } from '../../../../helpers/currency-helpers';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, inkBase, inkLight, skyLight, skyLightest } from '../../../../styles/design-tokens';

// Mind Map specific colors to match the design
const GOAL_GRADIENT_START = '#5E72E4';
const GOAL_GRADIENT_END = '#4C63D2';
const OBJECTIVE_COLOR = '#4CAF50';
const INITIATIVE_COLOR = '#FF9800';

type NodeType =
  | 'portfolio'
  | 'goal'
  | 'corporate-goal'
  | 'outcome'
  | 'initiative'
  | 'epic'
  | 'no-goals'
  | 'no-objectives';

type MindMapNodeData = {
  label: string;
  nodeType: NodeType;
  meta?: {
    key?: string;
    end_date?: string | null;
    progress?: number;
    cost?: number;
    budget?: number;
    status?: 'on_track' | 'at_risk' | 'off_track' | 'unknown' | string;
    contributing_teams?: string[];
  };
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

export const MindMapNode = memo(({ data }: NodeProps) => {
  const { t } = useTranslation();
  const nodeData = data as MindMapNodeData;
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  // Render different node types
  if (nodeData.nodeType === 'portfolio') {
    return (
      <>
        <PortfolioCard>
          <PortfolioTitle>{nodeData.label}</PortfolioTitle>
          <PortfolioSubtitle>{t('strategy.mind_map.strategic_goals_and_objectives')}</PortfolioSubtitle>
        </PortfolioCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  if (nodeData.nodeType === 'no-goals') {
    const isCollapsed = nodeData.isCollapsed || false;
    const onToggle = nodeData.onToggleCollapse;

    return (
      <>
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        <PlaceholderGoalCard>
          <PlaceholderHeader backgroundColor={GOAL_GRADIENT_START}>
            <HeaderBadge>{t('common.strategic_goal').toUpperCase()}</HeaderBadge>
            {onToggle && (
              <CollapseButton onClick={onToggle} type="button">
                {isCollapsed ? '▶' : '▼'}
              </CollapseButton>
            )}
          </PlaceholderHeader>
          <PlaceholderBody>
            <PlaceholderText>{nodeData.label}</PlaceholderText>
          </PlaceholderBody>
        </PlaceholderGoalCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  if (nodeData.nodeType === 'no-objectives') {
    const isCollapsed = nodeData.isCollapsed || false;
    const onToggle = nodeData.onToggleCollapse;

    return (
      <>
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        <PlaceholderOutcomeCard>
          <PlaceholderHeader backgroundColor={OBJECTIVE_COLOR}>
            <HeaderBadge>{t('common.outcome').toUpperCase()}</HeaderBadge>
            {onToggle && (
              <CollapseButton onClick={onToggle} type="button">
                {isCollapsed ? '▶' : '▼'}
              </CollapseButton>
            )}
          </PlaceholderHeader>
          <PlaceholderBody>
            <PlaceholderText>{nodeData.label}</PlaceholderText>
          </PlaceholderBody>
        </PlaceholderOutcomeCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  if (nodeData.nodeType === 'goal' || nodeData.nodeType === 'corporate-goal') {
    const meta = nodeData.meta || {};
    const progress = meta.progress || 0;
    const budget = meta.budget || 0;
    const isCollapsed = nodeData.isCollapsed || false;
    const onToggle = nodeData.onToggleCollapse;

    const formattedBudget = budget > 0 ? formatCurrency(budget, { currency: currencyCode }) : '-';

    return (
      <>
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        <GoalCard>
          <GoalCardHeader>
            <HeaderBadge>{t('common.strategic_goal').toUpperCase()}</HeaderBadge>
            {onToggle && (
              <CollapseButton onClick={onToggle} type="button">
                {isCollapsed ? '▶' : '▼'}
              </CollapseButton>
            )}
          </GoalCardHeader>
          <GoalCardBody>
            <GoalTitle>{nodeData.label}</GoalTitle>
            <GoalMetrics>
              <GoalMetric>
                <GoalMetricLabel>{t('common.progress')}:</GoalMetricLabel>
                <GoalMetricValue>{Math.round(progress)}%</GoalMetricValue>
              </GoalMetric>
              <GoalMetric>
                <GoalMetricLabel>{t('strategy.mind_map.budget')}:</GoalMetricLabel>
                <GoalMetricValue>{formattedBudget}</GoalMetricValue>
              </GoalMetric>
            </GoalMetrics>
          </GoalCardBody>
        </GoalCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  if (nodeData.nodeType === 'outcome') {
    const meta = nodeData.meta || {};
    const progress = meta.progress || 0;
    const status = meta.status || 'unknown';
    const isCollapsed = nodeData.isCollapsed || false;
    const onToggle = nodeData.onToggleCollapse;

    // Get status badge info
    const getStatusBadge = () => {
      switch (status) {
        case 'on_track':
          return { label: t('strategy.initiatives.statuses.on_track'), color: '#10b981' };
        case 'at_risk':
          return { label: t('strategy.initiatives.statuses.at_risk'), color: '#f59e0b' };
        case 'off_track':
          return { label: t('strategy.initiatives.statuses.off_track'), color: '#ef4444' };
        default:
          return { label: t('strategy.initiatives.statuses.unknown'), color: '#9ca3af' };
      }
    };

    const statusBadge = getStatusBadge();

    return (
      <>
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        <OutcomeCard>
          <OutcomeCardHeader>
            <HeaderLeft>
              <HeaderBadge>{t('common.outcome').toUpperCase()}</HeaderBadge>
            </HeaderLeft>
            {onToggle && (
              <CollapseButton onClick={onToggle} type="button">
                {isCollapsed ? '▶' : '▼'}
              </CollapseButton>
            )}
          </OutcomeCardHeader>
          <OutcomeCardBody>
            <OutcomeTitle>{nodeData.label}</OutcomeTitle>
            <OutcomeMetrics>
              <OutcomeProgress>
                {t('common.progress')}: {Math.round(progress)}%
              </OutcomeProgress>
              <StatusBadge statusColor={statusBadge.color}>{statusBadge.label}</StatusBadge>
            </OutcomeMetrics>
          </OutcomeCardBody>
        </OutcomeCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  if (nodeData.nodeType === 'initiative') {
    const meta = nodeData.meta || {};
    const progress = meta.progress || 0;
    const key = meta.key || '';
    const endDate = meta.end_date;
    const cost = meta.cost || 0;
    const contributingTeams = meta.contributing_teams || [];

    // Get progress color
    const getProgressColor = () => {
      if (progress >= 75) return '#10b981';
      if (progress >= 50) return '#f59e0b';
      return '#ef4444';
    };

    // Format date
    const formatDate = (dateStr: string | null | undefined) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formattedCost = formatCurrency(cost || 0, { currency: currencyCode });
    const formattedDate = formatDate(endDate);

    return (
      <>
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        <InitiativeCard>
          <InitiativeCardHeader>
            <HeaderBadge>{t('common.initiative').toUpperCase()}</HeaderBadge>
            {key && <InitiativeKey>#{key}</InitiativeKey>}
          </InitiativeCardHeader>
          <InitiativeCardBody>
            <InitiativeTitleRow>
              <InitiativeTitle>{nodeData.label}</InitiativeTitle>
              <InitiativeProgressBubble>{Math.round(progress)}%</InitiativeProgressBubble>
            </InitiativeTitleRow>
            <InitiativeMetaRow>
              <InitiativeMetaLabel>{t('common.target')}:</InitiativeMetaLabel>
              <InitiativeMetaValue>{formattedDate || t('strategy.mind_map.not_set')}</InitiativeMetaValue>
            </InitiativeMetaRow>
            <InitiativeMetaRow>
              <InitiativeMetaLabel>{t('strategy.mind_map.cost')}:</InitiativeMetaLabel>
              <InitiativeMetaValue>{formattedCost}</InitiativeMetaValue>
            </InitiativeMetaRow>
            {contributingTeams.length > 0 && (
              <ContributingTeams>
                {contributingTeams.slice(0, 3).map((team, idx) => (
                  <TeamTag key={idx}>{team}</TeamTag>
                ))}
                {contributingTeams.length > 3 && <TeamTag>+{contributingTeams.length - 3}</TeamTag>}
              </ContributingTeams>
            )}
            <ProgressBarContainer>
              <ProgressBar style={{ width: `${progress}%`, background: getProgressColor() }} />
            </ProgressBarContainer>
          </InitiativeCardBody>
        </InitiativeCard>
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      </>
    );
  }

  // Epic
  return (
    <>
      <Handle type="target" position={Position.Top} id="top" />
      <EpicCard>
        <CardBody>
          <CardTitle>{nodeData.label}</CardTitle>
        </CardBody>
      </EpicCard>
    </>
  );
});

// Portfolio Node
const PortfolioCard = styled.div`
  background: ${baseWhite};
  border-radius: 16px;
  padding: 24px 32px;
  width: 340px;
  min-height: 140px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border: 3px solid ${GOAL_GRADIENT_START};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const PortfolioTitle = styled.div`
  color: ${inkBase};
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 8px;
`;

const PortfolioSubtitle = styled.div`
  color: ${inkLight};
  font-size: 13px;
  font-weight: 500;
  text-align: center;
`;

// Goal Node - Blue header with white body
const GoalCard = styled.div`
  background: ${baseWhite};
  border-radius: 12px;
  width: 280px;
  min-height: 140px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  border: 1px solid #E3E8EF;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const GoalCardHeader = styled.div`
  background: linear-gradient(135deg, ${GOAL_GRADIENT_START} 0%, ${GOAL_GRADIENT_END} 100%);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GoalCardBody = styled.div`
  padding: 14px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${baseWhite};
`;

const GoalTitle = styled.div`
  color: ${inkBase};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const GoalMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
`;

const GoalMetric = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GoalMetricLabel = styled.div`
  color: ${inkLight};
  font-size: 12px;
  font-weight: 500;
`;

const GoalMetricValue = styled.div`
  color: ${inkBase};
  font-size: 13px;
  font-weight: 700;
`;

// Outcome Node - Green card matching the design
const OutcomeCard = styled.div`
  background: ${baseWhite};
  border-radius: 10px;
  width: 240px;
  min-height: 120px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid #81C784;
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const OutcomeCardHeader = styled.div`
  background: ${OBJECTIVE_COLOR};
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OutcomeCardBody = styled.div`
  padding: 12px 14px;
  background: ${baseWhite};
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const OutcomeTitle = styled.div`
  color: ${inkBase};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
`;

const OutcomeMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OutcomeProgress = styled.div`
  color: ${inkBase};
  font-size: 12px;
  font-weight: 600;
`;

const StatusBadge = styled.div<{ statusColor: string }>`
  background: ${(props) => props.statusColor};
  color: ${baseWhite};
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  text-align: center;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  width: fit-content;
`;

// Initiative Node - Orange card matching the design
const InitiativeCard = styled.div`
  background: ${baseWhite};
  border-radius: 8px;
  width: 240px;
  min-height: 110px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid #FFB74D;
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const InitiativeCardHeader = styled.div`
  background: ${INITIATIVE_COLOR};
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const InitiativeCardBody = styled.div`
  padding: 12px 14px;
  background: ${baseWhite};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const InitiativeTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

const InitiativeTitle = styled.div`
  color: ${inkBase};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  flex: 1;
`;

const InitiativeProgressBubble = styled.div`
  background: #10b981;
  color: ${baseWhite};
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  flex-shrink: 0;
`;

const InitiativeMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InitiativeMetaLabel = styled.div`
  color: ${inkLight};
  font-size: 11px;
  font-weight: 500;
`;

const InitiativeMetaValue = styled.div`
  color: ${inkBase};
  font-size: 11px;
  font-weight: 600;
`;

const ContributingTeams = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const TeamTag = styled.div`
  background: #E3F2FD;
  color: #1976D2;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  border: 1px solid #BBDEFB;
`;

// Epic Node
const EpicCard = styled.div`
  background: ${baseWhite};
  border-radius: 8px;
  width: 200px;
  min-height: 80px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border: 1px solid ${skyLight};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

// Placeholder Nodes
const PlaceholderGoalCard = styled.div`
  background: ${baseWhite};
  border-radius: 12px;
  width: 300px;
  height: 150px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 2px dashed ${skyLight};
  overflow: hidden;
  opacity: 0.6;
  display: flex;
  flex-direction: column;
`;

const PlaceholderOutcomeCard = styled.div`
  background: ${baseWhite};
  border-radius: 10px;
  width: 300px;
  height: 180px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 2px dashed ${skyLight};
  overflow: hidden;
  opacity: 0.6;
  display: flex;
  flex-direction: column;
`;

const PlaceholderHeader = styled.div<{ backgroundColor?: string }>`
  background: ${(props) => props.backgroundColor || skyLightest};
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PlaceholderBody = styled.div`
  padding: 16px;
  background: ${baseWhite};
  text-align: center;
`;

const PlaceholderText = styled.div`
  color: ${inkLight};
  font-size: 13px;
  font-weight: 500;
  font-style: italic;
`;

// Common Shared Components
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderBadge = styled.div`
  color: ${baseWhite};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const CollapseButton = styled.button`
  background: transparent;
  border: none;
  color: ${baseWhite};
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.2);
  }
`;

const CardBody = styled.div`
  padding: 12px 14px;
  background: ${baseWhite};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.div`
  color: ${baseWhite};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 8px;
`;

const InitiativeKey = styled.div`
  color: ${baseWhite};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background: ${skyLightest};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 3px;
`;
